import React, { useState, useEffect, useContext } from "react";
import { Typography, Grid, Container } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import axios from "axios";
import { DraftYearContext } from "../../contexts/DraftYearContext";
import { Link, useLocation } from "react-router-dom";
import PageLoader from "../../components/PageLoader/PageLoader";
import "./DraftOrder.css";


function PickRow({ pick }) {
    // draft_results.team_id (+ teams rti) is authoritative for who selected the player
    const hasPlayer = Boolean(pick.player_id);
    const logo = hasPlayer
        ? (pick.drafted_team_logo ?? pick.drafting_team_logo ?? pick.original_team_logo)
        : (pick.drafting_team_logo ?? pick.original_team_logo);

    return (
        <div className="draft-order-row">
            <span className="draft-order-num">{pick.pick}.</span>

            {pick.player_id && (
                <Link to={"/player/" + pick.player_id} className="draft-order-link">
                    <img src={logo} className="draft-order-logo" alt="" />
                    <span className="draft-order-name">{pick.first_name} {pick.last_name}</span>
                </Link>
            )}

            {!pick.player_id && !pick.drafting_team_name && (
                <Link to={"/team/" + pick.original_team_id} className="draft-order-link">
                    <img src={pick.original_team_logo} className="draft-order-logo" alt="" />
                    <span className="draft-order-name">{pick.original_team_name}</span>
                </Link>
            )}

            {!pick.player_id && pick.drafting_team_name && (
                <Link to={"/team/" + pick.drafting_team_id} className="draft-order-link">
                    <img src={pick.drafting_team_logo} className="draft-order-logo" alt="" />
                    <span className="draft-order-name">{pick.drafting_team_name}</span>
                    <Link to={"/team/" + pick.original_team_id} className="draft-order-via">
                        via {pick.original_team_name}
                    </Link>
                </Link>
            )}
        </div>
    );
}


function DraftOrder() {
    const location = useLocation();
    const onDraftResultsPath = location.pathname === "/draft-results";

    const [draftPicks, setDraftPicks] = useState([]);
    const [draftCompleted, setDraftCompleted] = useState(false);
    const [error, setError] = useState(null);
    const { draftYear } = useContext(DraftYearContext);

    const [loading, setLoading] = useState(true);
    const [draftYearChange, setDraftYearChange] = useState(true);
    const [prevDraftYear, setPrevDraftYear] = useState(null);

    useEffect(() => {
        setLoading(true);
        axios.get(process.env.REACT_APP_API_URL + "/draft/picks", { params: { "draft_year": draftYear } }).then((response) => {
            let picks = {};
            response.data.forEach((pick) => {
                if (pick.player_id) setDraftCompleted(true);
                if (!picks[pick.round]) picks[pick.round] = [pick];
                else picks[pick.round].push(pick);
            });
            setDraftPicks(picks);
            setLoading(false);
        }).catch((error) => setError(error));
    }, [draftYear]);

    useEffect(() => {
        if (prevDraftYear == null) setPrevDraftYear(draftYear);
        else if (prevDraftYear !== draftYear) setDraftYearChange(false);
        setPrevDraftYear(draftYear);
    }, [draftYear]);

    function getIsLoading() {
        return loading && !draftYearChange;
    }

    function splitIntoThirds(arr) {
        const third = Math.ceil(arr.length / 3);
        return [
            arr.slice(0, third),
            arr.slice(third, third * 2),
            arr.slice(third * 2),
        ];
    }

    const pageHeading = onDraftResultsPath
        ? "Draft Results"
        : draftCompleted
            ? "Draft Results"
            : "Draft Order";

    return (
        <>
            <Helmet>
                <title>{pageHeading} - Hockey Pipeline</title>
                <meta name="description" content="View the NHL draft order and draft results by round. Track which prospects were selected by which teams." />
                <link rel="canonical" href="https://hockeypipeline.com/draft-order" />
            </Helmet>
            <PageLoader loading={getIsLoading()} />
            <Container className="container top-container">
                <Grid container>
                    <Grid item xs={12}>
                        <Typography variant="h4" component="div" className="page-title">
                            {pageHeading}
                        </Typography>
                        <div className="title-underline" />
                    </Grid>

                    {draftPicks && Object.entries(draftPicks).map(([round, picks]) => {
                        const [col1, col2, col3] = splitIntoThirds(picks);
                        return (
                            <Grid container key={round} sx={{ width: '100%' }}>
                                <Grid item xs={12}>
                                    <Typography variant="h4" component="div" className="round-number">
                                        Round {round}
                                    </Typography>
                                </Grid>
                                {[col1, col2, col3].map((col, i) => (
                                    <Grid item xs={12} sm={6} md={4} key={i}>
                                        {col.map((pick) => <PickRow key={`${round}-${pick.pick}`} pick={pick} />)}
                                    </Grid>
                                ))}
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>
        </>
    );
}

export default DraftOrder;
